import PropTypes from 'prop-types';
import React, { useCallback, useContext } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import I18n from 'i18n-js';
import { Spin } from 'grab-ui';
import cx from 'classnames';
import circleApplied from 'public/icons/circle-green-check.svg';
import circleUnapplied from 'public/icons/circle-gray-empty.svg';
import EmptyOfferIcon from 'public/icons/offer-empty.svg';
import { getOfferID, isRewardOffer } from 'helper/anytime-booking';
import {
  fromUnixTime,
  formatDistance,
  differenceInCalendarDays,
  format,
} from 'date-fns';
import { OFFER_MAX_EXPIRED_DAY_DISPLAY_COUNT } from 'constants/anytime-booking-constants';
import promoIcon from 'public/icons/promo-icon.svg';
import clockIcon from 'public/icons/clock-orange.svg';
import warningIcon from 'public/icons/warning-yellow.svg';
import styles from './styles.less';
import OfferPanelContext from './offer-panel-context';

const renderCountDayLeft = (endUnixTime, hideEndTime) => {
  const endTime = fromUnixTime(endUnixTime);
  const expiredDateCount = differenceInCalendarDays(endTime, new Date());

  if (expiredDateCount <= OFFER_MAX_EXPIRED_DAY_DISPLAY_COUNT) {
    const countDaysLeft = formatDistance(endTime, new Date());
    return (
      <div>
        <img className={styles.infoIcon} src={clockIcon} alt="" />
        {countDaysLeft}
      </div>
    );
  }

  return (
    !hideEndTime && (
      <div className={cx(styles.offerExpiredDate)}>
        {I18n.t('phrase.offers.marketplace_offers_offers_valid_until_label')}{' '}
        {format(endTime, 'dd MMM yyyy')}
      </div>
    )
  );
};

const renderOfferDetail = offer => {
  const { count, offerType, endUnixTime } = offer;
  const isReward = isRewardOffer(offerType);

  return (
    <div className={styles.offerListInfo}>
      <div className={cx(styles.offerCount, styles.offerInfoColor)}>
        {isReward &&
          count > 0 && (
            <div className={styles.offerInfo}>
              <img className={styles.infoIcon} src={promoIcon} alt="" />
              {count}
            </div>
          )}
        {renderCountDayLeft(endUnixTime, isReward)}
      </div>
    </div>
  );
};

const OfferComponent = ({ offer, selected, errorMessage, isPromoSearch }) => {
  const {
    onViewOfferDetail,
    handleSelectOffer,
    handleScribeSelectOffer,
  } = useContext(OfferPanelContext);
  const { offerID, icon, name, validationMeta } = offer;
  const isOfferAvailable =
    _get(validationMeta, 'status', false) === 'AVAILABLE';
  const onClickSelectOffer = useCallback(
    () => {
      if (!errorMessage) {
        handleSelectOffer(offerID);
        handleScribeSelectOffer({ offerID, isPromoSearch, isOfferAvailable });
      }
    },
    [
      errorMessage,
      handleSelectOffer,
      handleScribeSelectOffer,
      isPromoSearch,
      isOfferAvailable,
    ],
  );

  return (
    <div className={styles.promoItemContainer} key={offerID}>
      <div
        className={cx(styles.promoItemInfo, {
          [styles.grayOut]: !!errorMessage,
        })}
        onClick={onViewOfferDetail(offerID)}
      >
        <img className={styles.icon} src={icon} alt="" />
        <div className={styles.content}>
          <div className={styles.contentName}>{name}</div>
          {errorMessage ? (
            <div className={styles.offerListInfo}>
              <div className={cx(styles.offerCount, styles.offerInfoColor)}>
                <div className={cx(styles.offerExpiredDate)}>
                  {errorMessage}
                </div>
              </div>
            </div>
          ) : (
            renderOfferDetail(offer)
          )}
        </div>
      </div>
      <div className={styles.promoApplied} onClick={onClickSelectOffer}>
        {errorMessage ? (
          !selected ? null : (
            <img
              className={styles.warningIconInList}
              src={warningIcon}
              alt=""
            />
          )
        ) : (
          <img src={selected ? circleApplied : circleUnapplied} alt="" />
        )}
      </div>
    </div>
  );
};

OfferComponent.defaultProps = {
  selected: false,
  isPromoSearch: false,
};

OfferComponent.propTypes = {
  offer: PropTypes.shape({}),
  selected: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  isPromoSearch: PropTypes.bool,
};

const OfferListComponent = ({
  offerConfig,
  offerList,
  selectedOffer,
  fetchingPromo,
  isPromoSearch,
}) => {
  const { errorMessage } = useContext(OfferPanelContext);

  const renderOfferList = useCallback(
    () => {
      if (_isEmpty(offerList)) {
        return (
          <div>
            <div className={styles.emptyOfferIconWrapper}>
              <img
                className={styles.emptyOfferIcon}
                src={EmptyOfferIcon}
                alt=""
              />
            </div>
            <div className={styles.emptyOfferText}>
              {I18n.t('v2.anytimeBooking.offers.empty')}
            </div>
          </div>
        );
      }
      const selectedOfferID = _get(selectedOffer, 'offerID', null);
      return offerList.map(offer => {
        const selected = getOfferID(offer) === selectedOfferID;
        const isOfferConfig =
          !!offerConfig && getOfferID(offerConfig) === getOfferID(offer);
        return (
          <OfferComponent
            key={getOfferID(offer)}
            offer={offer}
            selected={selected}
            errorMessage={isOfferConfig ? errorMessage : null}
            isPromoSearch={isPromoSearch}
          />
        );
      });
    },
    [offerList, offerConfig, errorMessage, selectedOffer, isPromoSearch],
  );

  return (
    <div className={styles.promoListContainer}>
      {fetchingPromo ? (
        <div className={styles.spinning}>
          <Spin style={{ margin: 'auto' }} />
        </div>
      ) : (
        renderOfferList()
      )}
    </div>
  );
};

OfferListComponent.defaultProps = {};

OfferListComponent.propTypes = {
  offerConfig: PropTypes.shape({}),
  offerList: PropTypes.arrayOf(PropTypes.shape({})),
  selectedOffer: PropTypes.shape({}),
  fetchingPromo: PropTypes.bool,
  isPromoSearch: PropTypes.bool,
};

export default OfferListComponent;
